import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Download, Copy, Megaphone, Smartphone, Sparkles } from "lucide-react";

const ADS = [
  ["ai-level", "AI Level", true], ["live-camber", "Live Lean Camber", true], ["visual-scan", "AI Visual Scan", true],
  ["ar-overlay", "AR Overlay", true], ["tire-wear", "AI Tire Diagnosis", true], ["caster-sweep", "Guided Caster"],
  ["garage", "150+ Vehicles"], ["corner-balance", "Corner Balance"], ["report", "Shareable Reports"],
  ["score", "Alignment Score"], ["compare", "Thortek vs Others", true],
];
const SCREENS = [
  "01-landing", "02-quick-align-ai-level", "03-quick-align-live-camber", "04-tilt-gauge-caster", "05-tilt-gauge-camber",
  "06-visual-scan", "07-ai-tire-wear", "08-garage", "09-spec-vault", "10-corner-balance", "11-shareable-report",
  "12-alignment-score", "13-ar-overlay",
];
const COPY = [
  { t: "Primary text — AI angle", c: "Your phone just became an alignment rack. Thortek's AI reads camber and toe from your camera, checks your floor is level before you measure, and draws the camber line right on your wheel in AR. 150+ factory specs. $199 once — no subscription. thortekperformance.com" },
  { t: "Primary text — DIY / off-road", c: "Lifted it? Leveled it? Stop paying $150 every time you touch the suspension. Lean your phone on the tire, read camber live, sweep caster step-by-step and say \"capture\" — hands-free. Thortek Performance. Pay once, align forever." },
  { t: "Primary text — shops", c: "Hand every customer a shareable before/after alignment report from your phone. AI tire-wear diagnosis, corner balance, VIN decode and 150+ factory specs in one app. One-time $199 license." },
  { t: "Headlines", c: "Shop-grade alignment. No rack.\nAI that reads your wheel angle.\nKnow your floor is level before you measure.\nCamber, caster & toe — in your driveway.\nPay once. Align forever." },
  { t: "Only-on-Thortek bullets", c: "• AI Visual Scan — camera measures camber & toe\n• AI Level — checks the ground before you measure\n• AR live camber overlay on your actual wheel\n• AI tire-wear diagnosis with cause + fix\n• Hands-free voice capture\n• 11 languages, one-time price" },
  { t: "Hashtags", c: "#wheelalignment #camber #DIYmechanic #offroad #liftedtrucks #Hummer #H2 #4x4 #trackday #autocross #carsetup #Thortek" },
];

const copyText = (s) => navigator.clipboard.writeText(s).then(() => toast.success("Copied"));

const Dl = ({ href, label, testId }) => (
  <a href={href} download data-testid={testId}
    className="inline-flex items-center gap-1.5 rounded-full border border-yellow-400/40 px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest text-yellow-400 hover:bg-yellow-400/10 transition-colors">
    <Download size={12} /> {label}
  </a>
);

const AdCard = ([slug, name, unique]) => (
  <div key={slug} data-testid={`adkit-ad-${slug}`} className="rounded-2xl border border-white/10 bg-surface/60 p-4">
    <div className="flex gap-3 items-end">
      <img src={`/ad-kit/promo/${slug}-square.jpg`} alt={name} className="flex-[1.6] min-w-0 aspect-square object-cover rounded-xl" loading="lazy" />
      <img src={`/ad-kit/promo/${slug}-story.jpg`} alt={name} className="flex-1 min-w-0 aspect-[9/16] object-cover rounded-xl" loading="lazy" />
    </div>
    <div className="mt-3 flex items-center justify-between gap-2 flex-wrap">
      <div className="font-heading font-bold">{name} {unique && <span className="ml-1 font-mono text-[9px] uppercase tracking-widest text-yellow-400">Only on Thortek</span>}</div>
      <div className="flex gap-2">
        <Dl href={`/ad-kit/promo/${slug}-square.jpg`} label="1080²" testId={`adkit-dl-${slug}-square`} />
        <Dl href={`/ad-kit/promo/${slug}-story.jpg`} label="1080×1920" testId={`adkit-dl-${slug}-story`} />
      </div>
    </div>
  </div>
);

export default function AdKit() {
  return (
    <div data-testid="adkit-page" className="min-h-screen bg-ink text-white px-4 sm:px-8 py-10">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.3em] text-yellow-400"><Megaphone size={14} /> Ad campaign kit</div>
        <h1 className="font-heading font-black uppercase text-4xl sm:text-5xl mt-2">Thortek ad kit</h1>
        <p className="text-slate-400 mt-3 max-w-2xl">Ready-to-post ads for Instagram, Facebook and TikTok (1080×1080 feed, 1080×1920 stories/reels), real app screenshots, and ad copy.</p>

        <h2 className="mt-12 mb-4 flex items-center gap-2 font-heading font-bold text-lg"><Sparkles size={18} className="text-yellow-400" /> Promo graphics</h2>
        <div className="grid md:grid-cols-2 gap-5">{ADS.map(AdCard)}</div>

        <h2 className="mt-14 mb-4 flex items-center gap-2 font-heading font-bold text-lg"><Smartphone size={18} className="text-yellow-400" /> Real app screenshots</h2>
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-4">
          {SCREENS.map((s) => (
            <a key={s} href={`/ad-kit/screens/${s}.png`} download data-testid={`adkit-screen-${s}`} className="group block">
              <img src={`/ad-kit/screens/${s}.png`} alt={s} loading="lazy" className="w-full aspect-[9/19.5] object-cover object-top rounded-xl border border-white/10 group-hover:border-yellow-400/60 transition-colors" />
              <div className="mt-1.5 font-mono text-[10px] text-slate-400 group-hover:text-yellow-400 truncate">{s.slice(3).replace(/-/g, " ")}</div>
            </a>
          ))}
        </div>

        <h2 className="mt-14 mb-4 font-heading font-bold text-lg">Ad copy</h2>
        <div className="grid md:grid-cols-2 gap-4">
          {COPY.map((x, i) => (
            <div key={i} data-testid={`adkit-copy-${i}`} className="rounded-2xl border border-white/10 bg-surface/60 p-5">
              <div className="flex items-center justify-between">
                <div className="font-mono text-[10px] uppercase tracking-widest text-yellow-400">{x.t}</div>
                <button onClick={() => copyText(x.c)} data-testid={`adkit-copy-btn-${i}`} className="text-slate-400 hover:text-yellow-400 transition-colors"><Copy size={15} /></button>
              </div>
              <p className="mt-3 text-sm text-slate-300 whitespace-pre-line leading-relaxed">{x.c}</p>
            </div>
          ))}
        </div>

        <div className="mt-12 text-center"><Link to="/" className="font-mono text-xs text-slate-500 hover:text-yellow-400 uppercase tracking-widest">← Back to site</Link></div>
      </div>
    </div>
  );
}
